import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import type { GbpConnectErrorInfo } from '../lib/gbpConnectErrors';

interface Props {
  error: GbpConnectErrorInfo | null;
  onClose: () => void;
  onRetry?: () => void;
}

export default function GoogleBusinessConnectErrorModal({
  error,
  onClose,
  onRetry,
}: Props) {
  if (!error) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100120] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        role="alertdialog"
        aria-labelledby="gbp-connect-error-title"
        aria-describedby="gbp-connect-error-message"
        className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p
                id="gbp-connect-error-title"
                className="text-sm font-semibold text-gray-900"
              >
                {error.title}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-mono truncate">
                {error.code}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-3">
          <p id="gbp-connect-error-message" className="text-sm text-gray-700 leading-relaxed">
            {error.message}
          </p>
          {error.tips.length > 0 ? (
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-3.5 py-3">
              <p className="text-xs font-semibold text-amber-900 mb-1.5">What to do</p>
              <ul className="space-y-1.5">
                {error.tips.map((tip) => (
                  <li key={tip} className="text-xs text-amber-900/90 leading-relaxed flex gap-2">
                    <span className="text-amber-500 mt-0.5 shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Close
          </button>
          {onRetry ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                onRetry();
              }}
              className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
            >
              Try again
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
